import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportedChatsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reported-chats?status=${filter}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      } else {
        toast.error('Failed to load reports');
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!action) {
      toast.error('Please select an action');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reported-chats/${selectedReport.id}/action`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes })
      });

      if (res.ok) {
        toast.success(`Action '${action}' taken successfully`);
        setSelectedReport(null);
        setAction('');
        setNotes('');
        loadReports();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to take action');
      }
    } catch (error) {
      toast.error('Failed to take action');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      under_review: { color: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      dismissed: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };

    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;

    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Reported Chats</h1>
          <div className="flex gap-2">
            {['pending', 'under_review', 'resolved', 'dismissed'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
              >
                {f.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading reports...</div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No {filter} reports found
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <Card key={report.id} className="hover:border-blue-300 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">
                        {report.conversation_type === 'dm' ? 'Direct Message' : 'Group Chat'} Report
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Reported by {report.reporter_name} • {formatDate(report.created_at)}
                      </p>
                    </div>
                    {getStatusBadge(report.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold">Reason:</p>
                    <p className="text-sm text-muted-foreground">{report.reason}</p>
                  </div>
                  
                  {report.details && (
                    <div>
                      <p className="text-sm font-semibold">Details:</p>
                      <p className="text-sm text-muted-foreground">{report.details}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold">Participants:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {report.participants.map((p) => (
                        <Badge key={p.user_id} variant="secondary">
                          {p.name || 'Unknown'}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {report.admin_notes && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <p className="text-sm font-semibold">Admin Notes:</p>
                      <p className="text-sm text-muted-foreground">{report.admin_notes}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Action taken: {report.action_taken} • {formatDate(report.reviewed_at)}
                      </p>
                    </div>
                  )}

                  {report.status === 'pending' && (
                    <div className="pt-2">
                      <Button
                        onClick={() => setSelectedReport(report)}
                        size="sm"
                      >
                        Take Action
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Action Dialog */}
        <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Take Action on Report</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-2">Select Action:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="action"
                      value="warn"
                      checked={action === 'warn'}
                      onChange={(e) => setAction(e.target.value)}
                    />
                    <span className="text-sm">Warn users (send notification)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="action"
                      value="suspend"
                      checked={action === 'suspend'}
                      onChange={(e) => setAction(e.target.value)}
                    />
                    <span className="text-sm">Suspend users (7 days)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="action"
                      value="resolve"
                      checked={action === 'resolve'}
                      onChange={(e) => setAction(e.target.value)}
                    />
                    <span className="text-sm">Resolve (issue addressed)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="action"
                      value="dismiss"
                      checked={action === 'dismiss'}
                      onChange={(e) => setAction(e.target.value)}
                    />
                    <span className="text-sm">Dismiss (no action needed)</span>
                  </label>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Admin Notes:</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedReport(null)}>
                Cancel
              </Button>
              <Button onClick={handleAction} disabled={!action || submitting}>
                {submitting ? 'Processing...' : 'Submit Action'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
